import React, { useEffect } from 'react';
import SidebarScreen from '@screens/Sidebar/SidebarScreen';
import ProfileScreen from '@screens/Profile/ProfileScreen';
import { HorizontalContainer, VerticalContainer } from '@shared/designSystem/Containers';
import { designSystemStyles } from '@shared/styles/designSystemStyles';
import EmptyContent from '@components/General/EmptyContent';
import { Navigate, Routes, Route, Outlet } from 'react-router-dom';

import ResumenControlBoard from '@screens/Resumen/ResumenControlBoard';
import PresenteeismTableControlBoard from '@screens/Listado/PresenteeismTableControlBoard';
import MapDashboardControlBoard from '@screens/Mapa/MapDashboardControlBoard';
import ColaboradoresControlBoard from '@screens/Colaboradores/ColaboradoresControlBoard';
import AppAdoptionScreen from '@screens/AdopcionApp/AppAdoptionScreen';
import ColaboradorControlBoard from '@screens/Colaboradores/ColaboradorControlBoard';
import QontactPointControlBoard from '@screens/QontactPoints/QontactPointScreen';
import QontactPointDetailControlBoard from '@screens/QontactPoints/QontactPointDetailScreen';
import { TurnosProvider } from '@contexts/TurnosContext';
import NovedadesScreen from '@screens/Novedades/NovedadesScreen';
import ReporteScreen from '@screens/Novedades/ReporteScreen';
import NotificacionesScreen from '@screens/Notificaciones/NotificacionesScreen';
import ConfiguracionScreen from '@screens/Configuracion/ConfiguracionScreen';
import ConfiguracionNovedades from '@components/Configuracion/Novedades/ConfiguracionNovedades';
import TurnoScreen from "@screens/QontactPoints/TurnoScreen"
import ReportesScreen from '@screens/Novedades/ReportesScreen';
import RosterTabScreen from '@screens/Roster/RosterTabScreen';

import { useAuth } from '@contexts/authContext';
import PopupDisplayer from '@components/General/PopupDisplayer';
import ComunicacionesScreen from '@screens/Comunicaciones/ComunicacionesScreen';
import SolicitudesScreen from '@screens/Solicitudes/SolicitudesScreen';
import AlertasScreen from '@screens/Alertas/AlertasScreen';
import { ConfiguracionUsuarios } from '@screens/Configuracion/Usuarios/ConfiguracionUsuarios';
import { ConfiguracionRol } from '@screens/Configuracion/Usuarios/ConfiguracionRol';
import ConfiguracionColaboradoresScreen from '@screens/Configuracion/Usuarios/ConfiguracionColaboradoresScreen';
import ConfiguracionCamposQontactPointScreen from '@screens/Configuracion/CamposPersonalizados/ConfiguracionCamposQontactPointScreen';
import { AppRoutes } from '@models/AppRoutes';
import { Application } from '@core/Application';
import { QontactUser } from '@models/QontactUser';
import { AnalyticsHelper } from '@core/Analytics/AnalyticsHelper';
import HourBankScreen from '@screens/BancoDeHoras/HourBankScreen';
import HourBankDetailScreen from '@screens/BancoDeHoras/HourBankDetailScreen';
import PilotOnboardingScreen from '@screens/Onboarding/PilotOnboardingScreen';
import { canAccessPilotOnboarding } from '@screens/Onboarding/PilotOnboardingAccess';
import ColorInputShowcaseScreen from '@shared/showcaseScreens/ColorInputShowcaseScreen';
import MilestoneStepsShowcaseScreen from '@shared/showcaseScreens/MilestoneStepsShowcaseScreen';
import SelectedSearchInputShowcaseScreen from '@shared/showcaseScreens/SelectedSearchInputShowcaseScreen';
import FullScreenModalShowcaseScreen from '@shared/showcaseScreens/FullScreenModalShowcaseScreen';
import TabSleekShowcaseScreen from '@shared/showcaseScreens/TabSleekShowcaseScreen';
import FilterPillShowcaseScreen from '@shared/showcaseScreens/FilterPillShowcaseScreen';
import SystemIconShowcaseScreen from '@shared/showcaseScreens/SystemIconShowcaseScreen';
import LeftMenuSectionShowcaseScreen from '@shared/showcaseScreens/LeftMenuSectionShowcaseScreen';
import NoticeBannerShowcaseScreen from '@shared/showcaseScreens/NoticeBannerShowcaseScreen';
import { useSelectedOrganization } from '@contexts/useSelectedOrganization';
import { PlatformNoticesBar } from '@screens/PlatformNotices/PlatformNoticesBar';

const Dashboard = () => {

    const { orgs, user, updateUserRole, isSystemAdmin } = useAuth();
    const { selectedOrganization: org, setSelectedOrganization: setOrg } = useSelectedOrganization(orgs);
    const canShowPilotOnboarding = canAccessPilotOnboarding({
        env: process.env.REACT_APP_FRONT_ENV,
        organizationID: org?.id,
        isSystemAdmin,
    });

    useEffect(() => {
        if (org) { 
           updateUserRole(org.id)
        }
    }, [org]);

    useEffect(() => {
        if (!org || !user) {
            return;
        }

        let analytics = Application.shared().coreDependencies.analytics

        analytics.setSelectedOrganizationID(org.id)
        let qontactUser = new QontactUser(user)
        analytics.setUser(qontactUser)
        AnalyticsHelper.trackMainScreenShown()
    }, [user, org])

    return (
    <HorizontalContainer
        gap="0"
        alignment="stretch"
        height="100vh"
        width="100%"
    >
        <SidebarScreen organization={org} onOrganizationSelected={setOrg} />
        <div style={{ flex: 1, minWidth: 0, height: "100%" }}>
        {/* No gap: the notices bar must sit flush on the page and take 0px when empty. */}
        <VerticalContainer
            height="100%"
            width="100%"
            gap={designSystemStyles.gapNone}
            backgroundColor={designSystemStyles.colorBackgroundGray3}
        >
        <PlatformNoticesBar organization={org} organizationCount={orgs?.length ?? 0} uid={user?.uid} />
        <Routes>
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/resumen" element={<ResumenControlBoard org={org} />} />
            {/* <Route path="/frecuencia" element={<FrequencyControlBoard org={org} />} /> */}
            <Route path="/listado" element={<PresenteeismTableControlBoard org={org} />} />
            <Route path="/roster" element={<RosterTabScreen org={org} />} />
            <Route path="/mapa" element={<MapDashboardControlBoard org={org} />} />
            <Route path="/colaboradores" element={<ColaboradoresControlBoard org={org} />}/>
            <Route path="/colaborador" element={<ColaboradorControlBoard org={org} />} />
            <Route path="/qontact-app" element={<AppAdoptionScreen org={org} />} />
            <Route path="/qontactPoints" element={<QontactPointControlBoard org={org}/>} />
            <Route element={<TurnosLayout />}>
                <Route path="/qontactPoint" element={<QontactPointDetailControlBoard org={org} />} />
                <Route path="/turno" element={<TurnoScreen org={org} />} />
            </Route>
            <Route path="/notificaciones" element={<NotificacionesScreen org={org}/>} />
            <Route path="/comunicaciones" element={<ComunicacionesScreen org={org}/>} />
            <Route path="/alertas" element={<AlertasScreen org={org}/>} />
            <Route path="/novedades" element={<NovedadesScreen org={org}/>} />
            <Route path={AppRoutes.configuracion} element={<ConfiguracionScreen org={org}/>} />
            <Route path="/configuracion/novedades" element={<ConfiguracionNovedades org={org}/>} />
            <Route path='/configuracion/usuarios' element={<ConfiguracionUsuarios org={org}/>} />
            <Route
                path={AppRoutes.configuracionCamposColaborador}
                element={<ConfiguracionColaboradoresRoute org={org} />}
            />
            <Route
                path={AppRoutes.configuracionCamposQontactPoint}
                element={<ConfiguracionCamposQontactPointRoute org={org} />}
            />
            <Route path='/configuracion/usuarios/rol' element={<ConfiguracionRol org={org}/>} />
            <Route path="/reportes" element={<ReportesScreen org={org}/>} />
            <Route path="/reporte" element={<ReporteScreen org={org}/>} />
            <Route path="/bancoDeHoras" element={<HourBankScreen org={org}/>} />
            <Route path="/bancoDeHoras/detalle" element={<HourBankDetailScreen org={org}/>} />
            <Route path="/solicitudes" element={<SolicitudesScreen org={org}/>} />
            <Route path="/showcase/color-input" element={<ColorInputShowcaseScreen />} />
            <Route path="/showcase/milestone-steps" element={<MilestoneStepsShowcaseScreen />} />
            <Route path="/showcase/selected-search" element={<SelectedSearchInputShowcaseScreen />} />
            <Route path="/showcase/fullscreen-modal" element={<FullScreenModalShowcaseScreen />} />
            <Route path="/showcase/tabs" element={<TabSleekShowcaseScreen />} />
            <Route path="/showcase/filter-pills" element={<FilterPillShowcaseScreen />} />
            <Route path="/showcase/system-icons" element={<SystemIconShowcaseScreen />} />
            <Route path="/showcase/left-menu-section" element={<LeftMenuSectionShowcaseScreen />} />
            <Route path="/showcase/notice-banner" element={<NoticeBannerShowcaseScreen />} />
            {canShowPilotOnboarding && (
                <Route path="/onboarding/pilot" element={<PilotOnboardingScreen org={org} />} />
            )}
            <Route path="*" element={<EmptyContent />} />
        </Routes>
        </VerticalContainer>
        </div>
        <PopupDisplayer />
    </HorizontalContainer>
  );
}

// For context purposes
const TurnosLayout = () => (
    <TurnosProvider>
      <Outlet />
    </TurnosProvider>
  );

const ConfiguracionColaboradoresRoute = ({ org }) => {
    if (!org) {
        return <EmptyContent />;
    }

    if (!org.newFlags?.AdminUserCreationWeb) {
        return <Navigate to={AppRoutes.configuracion} replace />;
    }

    return <ConfiguracionColaboradoresScreen orgID={org.id} />;
};

// Same flag as the Qontact Points section itself
const ConfiguracionCamposQontactPointRoute = ({ org }) => {
    if (!org) {
        return <EmptyContent />;
    }

    if (!org.newFlags?.QontactPointCreationWeb) {
        return <Navigate to={AppRoutes.configuracion} replace />;
    }

    return <ConfiguracionCamposQontactPointScreen orgID={org.id} />;
};

export default Dashboard;
